import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SEO from "./SEO";
import { supabase } from "../lib/supabase";
import { useAppData } from "../context/AppDataContext";
import { updateTour, createTour, deleteTour } from "../services/supabaseTours";
import { updateTravelogue, createTravelogue, deleteTravelogue } from "../services/supabaseTravelogues";
import { updateDestination, createDestination, deleteDestination } from "../services/supabaseDestinations";
import { uploadImage } from "../services/supabaseStorage";

const TOUR_TITLES = {
  "valladolid-1tjedan": {
    hr: "Valladolid učenje španjolskog jezika",
    en: "Valladolid – Spanish language course (August)",
    de: "Valladolid – Spanischkurs (August)",
    es: "Valladolid – curso de español (agosto)",
  },
  "valladolid-2tjedan": {
    hr: "Valladolid učenje španjolskog jezika",
    en: "Valladolid – Spanish language course (2 weeks, August)",
    de: "Valladolid – Spanischkurs (2 Wochen, August)",
    es: "Valladolid – curso de español (2 semanas, agosto)",
  },
  "valladolid-1tjedan-lipanj": {
    hr: "Valladolid učenje španjolskog jezika (13.–21. 6. 2026.)",
    en: "Valladolid – Spanish course (13–21 June 2026)",
    de: "Valladolid – Spanischkurs (13.–21. Juni 2026)",
    es: "Valladolid – curso de español (13–21 jun 2026)",
  },
  "valladolid-2tjedan-lipanj": {
    hr: "Valladolid učenje španjolskog jezika (13.–27. 6. 2026., 2 tjedna)",
    en: "Valladolid – Spanish course (13–27 June 2026, 2 weeks)",
    de: "Valladolid – Spanischkurs (13.–27. Juni 2026, 2 Wochen)",
    es: "Valladolid – curso de español (13–27 jun 2026, 2 semanas)",
  },
  "valladolid-san-lorenzo-rujan": {
    hr: "Valladolid – Fiesta San Lorenzo (rujan 2026)",
    en: "Valladolid – Fiesta San Lorenzo (September 2026)",
    de: "Valladolid – Fiesta San Lorenzo (September 2026)",
    es: "Valladolid – Fiesta San Lorenzo (septiembre 2026)",
  },
  alicante: { hr: "Alicante", en: "Alicante", de: "Alicante", es: "Alicante" },
  "alicante-maj": { hr: "Alicante (svibanj)", en: "Alicante (May)", de: "Alicante (Mai)", es: "Alicante (mayo)" },
  "alicante-ozujak": { hr: "Alicante (ožujak)", en: "Alicante (March)", de: "Alicante (März)", es: "Alicante (marzo)" },
  "alicante-listopad": { hr: "Alicante (listopad)", en: "Alicante (October)", de: "Alicante (Oktober)", es: "Alicante (octubre)" },
  valencia: {
    hr: "Valencia – Las Fallas (privatni aranžman)",
    en: "Valencia – Las Fallas (private arrangement)",
    de: "Valencia – Las Fallas (privates Arrangement)",
    es: "Valencia – Las Fallas (arreglo privado)",
  },
  "od-madrida-do-vinograda": {
    hr: "Od Madrida do vinograda",
    en: "From Madrid to Vineyards",
    de: "Von Madrid zu den Weinbergen",
    es: "De Madrid a los viñedos",
  },
};

const hasSupabase = !!(process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { tours: localTours, refreshTours, refreshTravelogues, refreshDestinations, travelogues, destinations } = useAppData();
  const [editingTour, setEditingTour] = useState(null);
  const [editingTravelogue, setEditingTravelogue] = useState(null);
  const [editingDestination, setEditingDestination] = useState(null);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("tours");
  const [authChecked, setAuthChecked] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadingTravelogueImage, setUploadingTravelogueImage] = useState(false);
  const [uploadingDestinationImage, setUploadingDestinationImage] = useState(false);

  useEffect(() => {
    if (!hasSupabase) {
      navigate("/admin/login");
      setAuthChecked(true);
      return;
    }
    let cancelled = false;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      if (!session) {
        localStorage.removeItem("travelina_admin_auth");
        localStorage.removeItem("travelina_admin_user");
        navigate("/admin/login");
      } else {
        const u = session.user;
        localStorage.setItem("travelina_admin_user", JSON.stringify({ email: u?.email, name: u?.email }));
      }
      setAuthChecked(true);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        localStorage.removeItem("travelina_admin_auth");
        localStorage.removeItem("travelina_admin_user");
        navigate("/admin/login");
      }
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleLogout = async () => {
    if (hasSupabase) await supabase.auth.signOut();
    localStorage.removeItem("travelina_admin_auth");
    localStorage.removeItem("travelina_admin_user");
    navigate("/admin/login");
  };

  const currentUser = JSON.parse(localStorage.getItem("travelina_admin_user") || "{}");

  const handleEdit = (tour) => {
    setEditingTour({ ...tour });
  };

  const handleSaveTour = async () => {
    if (!hasSupabase) return;
    const res = editingTour.id ? await updateTour(editingTour) : await createTour(editingTour);
    if (res.error) {
      console.error(res.error);
      return;
    }
    await refreshTours();
    setEditingTour(null);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleCancel = () => {
    setEditingTour(null);
    setEditingTravelogue(null);
    setEditingDestination(null);
  };

  const handleSaveTravelogue = async () => {
    if (!hasSupabase) return;
    const res = editingTravelogue.id ? await updateTravelogue(editingTravelogue) : await createTravelogue(editingTravelogue);
    if (res.error) {
      console.error(res.error);
      alert(res.error.message || "Spremanje putopisa nije uspjelo. Provjeri konzolu i Supabase pravila (RLS / stupci).");
      return;
    }
    await refreshTravelogues();
    setEditingTravelogue(null);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleSaveDestination = async () => {
    if (!hasSupabase) return;
    const res = editingDestination.id ? await updateDestination(editingDestination) : await createDestination(editingDestination);
    if (res.error) {
      console.error(res.error);
      alert(res.error.message || "Spremanje destinacije nije uspjelo. Ako nedostaje stupac image, pokreni SQL migraciju iz supabase/schema.sql.");
      return;
    }
    await refreshDestinations();
    setEditingDestination(null);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleChange = (field, value) => {
    setEditingTour({ ...editingTour, [field]: value });
  };

  const handleBlogTextChange = (lang, value) => {
    setEditingTour({
      ...editingTour,
      blogText: {
        ...editingTour.blogText,
        [lang]: value,
      },
    });
  };

  const handleDetailedInfoChange = (lang, value) => {
    setEditingTour({
      ...editingTour,
      detailedInfo: {
        ...(editingTour.detailedInfo || {}),
        [lang]: value,
      },
    });
  };

  const handleGalleryChange = (value) => {
    // Pretvori string u array (svaki red je jedna slika)
    const galleryArray = value
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    setEditingTour({
      ...editingTour,
      gallery: galleryArray,
    });
  };

  if (!authChecked) {
    return (
      <div className="app admin-app" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
        <p style={{ color: "#64748b" }}>Učitavanje...</p>
      </div>
    );
  }

  if (editingTour) {
    return (
      <div className="app admin-app">
        <SEO title="Admin Dashboard - TraveLina" noindex={true} />
        <Navbar />
        <main>
          <section className="section" style={{ paddingTop: "6rem" }}>
            <div className="container">
              <div style={{ marginBottom: "2rem" }}>
                <button
                  onClick={handleCancel}
                  style={{
                    padding: "0.5rem 1rem",
                    background: "#f1f5f9",
                    border: "1px solid #cbd5e1",
                    borderRadius: "0.5rem",
                    color: "#334155",
                    cursor: "pointer",
                    marginRight: "1rem",
                  }}
                >
                  ← Natrag
                </button>
                <button
                  onClick={handleSaveTour}
                  style={{
                    padding: "0.5rem 1rem",
                    background: "#f97316",
                    border: "none",
                    borderRadius: "0.5rem",
                    color: "#111827",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                >
                  Spremi promjene
                </button>
              </div>

              <div
                style={{
                  background: "#ffffff",
                  padding: "2rem",
                  borderRadius: "1.5rem",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
                }}
              >
                <h2 style={{ marginBottom: "1.5rem" }}>
                  Uređivanje: {TOUR_TITLES[editingTour.key]?.hr || editingTour.key || "Novo putovanje"}
                </h2>

                <div style={{ display: "grid", gap: "1.5rem" }}>
                  {(!editingTour.id || !editingTour.key) && (
                    <div>
                      <label style={{ display: "block", marginBottom: "0.5rem", color: "#64748b" }}>Key (jedinstveni id, npr. alicante, valladolid)</label>
                      <input
                        type="text"
                        value={editingTour.key || ""}
                        onChange={(e) => handleChange("key", e.target.value)}
                        style={{ width: "100%", padding: "0.75rem", background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "0.5rem", color: "#111827" }}
                        placeholder="npr. barcelona"
                      />
                    </div>
                  )}
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: "#64748b",
                      }}
                    >
                      Kategorija
                    </label>
                    <select
                      value={editingTour.category}
                      onChange={(e) => handleChange("category", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "0.75rem",
                        background: "#ffffff",
                        border: "1px solid #cbd5e1",
                        borderRadius: "0.5rem",
                        color: "#111827",
                      }}
                    >
                      <option value="city">Gradovi</option>
                      <option value="beach">More</option>
                      <option value="adventure">Avantura</option>
                      <option value="romantic">Romantično</option>
                    </select>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "0.5rem",
                          color: "#64748b",
                        }}
                      >
                        Dana
                      </label>
                      <input
                        type="number"
                        value={editingTour.days}
                        onChange={(e) => handleChange("days", parseInt(e.target.value))}
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          background: "#ffffff",
                          border: "1px solid #cbd5e1",
                          borderRadius: "0.5rem",
                          color: "#111827",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "0.5rem",
                          color: "#64748b",
                        }}
                      >
                        Noćenja
                      </label>
                      <input
                        type="number"
                        value={editingTour.nights}
                        onChange={(e) => handleChange("nights", parseInt(e.target.value))}
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          background: "#ffffff",
                          border: "1px solid #cbd5e1",
                          borderRadius: "0.5rem",
                          color: "#111827",
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: "#64748b",
                      }}
                    >
                      Država
                    </label>
                    <input
                      type="text"
                      value={editingTour.country}
                      onChange={(e) => handleChange("country", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "0.75rem",
                        background: "#ffffff",
                        border: "1px solid #cbd5e1",
                        borderRadius: "0.5rem",
                        color: "#111827",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: "#64748b",
                      }}
                    >
                      Datum
                    </label>
                    <input
                      type="date"
                      value={editingTour.date}
                      onChange={(e) => handleChange("date", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "0.75rem",
                        background: "#ffffff",
                        border: "1px solid #cbd5e1",
                        borderRadius: "0.5rem",
                        color: "#111827",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: "#64748b",
                      }}
                    >
                      Cijena (€)
                    </label>
                    <input
                      type="number"
                      value={editingTour.price}
                      onChange={(e) => handleChange("price", parseInt(e.target.value))}
                      style={{
                        width: "100%",
                        padding: "0.75rem",
                        background: "#ffffff",
                        border: "1px solid #cbd5e1",
                        borderRadius: "0.5rem",
                        color: "#111827",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: "#64748b",
                      }}
                    >
                      Tag
                    </label>
                    <input
                      type="text"
                      value={editingTour.tag}
                      onChange={(e) => handleChange("tag", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "0.75rem",
                        background: "#ffffff",
                        border: "1px solid #cbd5e1",
                        borderRadius: "0.5rem",
                        color: "#111827",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: "#64748b",
                      }}
                    >
                      Glavna slika
                    </label>
                    {editingTour.image && (
                      <div style={{ marginBottom: "0.75rem" }}>
                        <img
                          src={editingTour.image}
                          alt="Pregled"
                          style={{ maxWidth: "200px", maxHeight: "120px", objectFit: "cover", borderRadius: "0.5rem", border: "1px solid #e2e8f0" }}
                        />
                      </div>
                    )}
                    {hasSupabase && (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          id="tour-image-upload"
                          style={{ display: "none" }}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setUploadingImage(true);
                            const folder = `tours/${(editingTour.key || "new").replace(/\s+/g, "-")}`;
                            const { url, error } = await uploadImage(folder, file);
                            setUploadingImage(false);
                            e.target.value = "";
                            if (error) return alert(error.message || "Upload nije uspio.");
                            if (url) handleChange("image", url);
                          }}
                        />
                        <button
                          type="button"
                          disabled={uploadingImage}
                          onClick={() => document.getElementById("tour-image-upload").click()}
                          style={{
                            padding: "0.5rem 1rem",
                            marginBottom: "0.5rem",
                            background: uploadingImage ? "#6b7280" : "#3b82f6",
                            border: "none",
                            borderRadius: "0.5rem",
                            color: "#fff",
                            cursor: uploadingImage ? "not-allowed" : "pointer",
                            fontSize: "0.9rem",
                          }}
                        >
                          {uploadingImage ? "Uploadam…" : "Upload sliku s računala"}
                        </button>
                      </>
                    )}
                    <input
                      type="text"
                      value={editingTour.image}
                      onChange={(e) => handleChange("image", e.target.value)}
                      placeholder={hasSupabase ? "Ili zalijepi URL (npr. od Supabasea)" : "Putanja ili URL slike"}
                      style={{
                        width: "100%",
                        padding: "0.75rem",
                        background: "#ffffff",
                        border: "1px solid #cbd5e1",
                        borderRadius: "0.5rem",
                        color: "#111827",
                      }}
                    />
                  </div>

                  <div style={{ marginTop: "2rem" }}>
                    <h3 style={{ marginBottom: "1rem" }}>Blog tekstovi</h3>
                    {["hr", "en", "de", "es"].map((lang) => (
                      <div key={lang} style={{ marginBottom: "1.5rem" }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "0.5rem",
                            color: "#64748b",
                            textTransform: "uppercase",
                          }}
                        >
                          {lang === "hr" ? "Hrvatski" : lang === "en" ? "English" : lang === "de" ? "Deutsch" : "Español"}
                        </label>
                        <textarea
                          value={editingTour.blogText?.[lang] || ""}
                          onChange={(e) => handleBlogTextChange(lang, e.target.value)}
                          rows={8}
                          style={{
                            width: "100%",
                            padding: "0.75rem",
                            background: "#ffffff",
                            border: "1px solid #cbd5e1",
                            borderRadius: "0.5rem",
                            color: "#111827",
                            fontFamily: "inherit",
                            resize: "vertical",
                          }}
                          placeholder={`Unesite blog tekst na ${lang === "hr" ? "hrvatskom" : lang === "en" ? "engleskom" : lang === "de" ? "njemačkom" : "španjolskom"} jeziku...`}
                        />
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: "2rem" }}>
                    <h3 style={{ marginBottom: "1rem" }}>Galerija fotografija</h3>
                    {(editingTour.gallery || []).length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1rem" }}>
                        {(editingTour.gallery || []).map((url, idx) => (
                          <div key={idx} style={{ position: "relative" }}>
                            <img
                              src={url}
                              alt={`Galerija ${idx + 1}`}
                              style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "0.5rem", border: "1px solid #e2e8f0" }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const next = [...(editingTour.gallery || [])];
                                next.splice(idx, 1);
                                setEditingTour({ ...editingTour, gallery: next });
                              }}
                              style={{
                                position: "absolute",
                                top: "4px",
                                right: "4px",
                                width: "24px",
                                height: "24px",
                                padding: 0,
                                background: "rgba(239,68,68,0.9)",
                                border: "none",
                                borderRadius: "50%",
                                color: "#fff",
                                cursor: "pointer",
                                fontSize: "1rem",
                                lineHeight: 1,
                              }}
                              aria-label="Ukloni sliku"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {hasSupabase && (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          id="tour-gallery-upload"
                          style={{ display: "none" }}
                          onChange={async (e) => {
                            const files = e.target.files;
                            if (!files?.length) return;
                            setUploadingGallery(true);
                            const folder = `tours/${(editingTour.key || "new").replace(/\s+/g, "-")}/gallery`;
                            const current = editingTour.gallery || [];
                            for (let i = 0; i < files.length; i++) {
                              const { url, error } = await uploadImage(folder, files[i]);
                              if (!error && url) current.push(url);
                            }
                            setUploadingGallery(false);
                            e.target.value = "";
                            setEditingTour({ ...editingTour, gallery: current });
                          }}
                        />
                        <button
                          type="button"
                          disabled={uploadingGallery}
                          onClick={() => document.getElementById("tour-gallery-upload").click()}
                          style={{
                            padding: "0.5rem 1rem",
                            background: uploadingGallery ? "#6b7280" : "#3b82f6",
                            border: "none",
                            borderRadius: "0.5rem",
                            color: "#fff",
                            cursor: uploadingGallery ? "not-allowed" : "pointer",
                            fontSize: "0.9rem",
                          }}
                        >
                          {uploadingGallery ? "Uploadam…" : "Dodaj slike (upload s računala)"}
                        </button>
                        <p style={{ marginTop: "0.5rem", fontSize: "0.75rem", color: "#64748b" }}>
                          Možete odabrati više slika odjednom.
                        </p>
                      </>
                    )}
                    <div style={{ marginTop: "1rem" }}>
                      <label style={{ display: "block", marginBottom: "0.5rem", color: "#64748b", fontSize: "0.85rem" }}>
                        Ili putanje slika (jedna po redu)
                      </label>
                      <textarea
                        value={(editingTour.gallery || []).join("\n")}
                        onChange={(e) => handleGalleryChange(e.target.value)}
                        rows={3}
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          background: "#ffffff",
                          border: "1px solid #cbd5e1",
                          borderRadius: "0.5rem",
                          color: "#111827",
                          fontFamily: "monospace",
                          fontSize: "0.85rem",
                          resize: "vertical",
                        }}
                        placeholder="/images/slika1.jpg&#10;/images/slika2.jpg"
                      />
                    </div>
                  </div>

                  <div style={{ marginTop: "2rem" }}>
                    <h3 style={{ marginBottom: "1rem" }}>Detaljne informacije o putovanju</h3>
                    {["hr", "en", "de", "es"].map((lang) => (
                      <div key={lang} style={{ marginBottom: "1.5rem" }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "0.5rem",
                            color: "#64748b",
                            textTransform: "uppercase",
                          }}
                        >
                          {lang === "hr" ? "Hrvatski" : lang === "en" ? "English" : lang === "de" ? "Deutsch" : "Español"}
                        </label>
                        <textarea
                          value={editingTour.detailedInfo?.[lang] || ""}
                          onChange={(e) => handleDetailedInfoChange(lang, e.target.value)}
                          rows={12}
                          style={{
                            width: "100%",
                            padding: "0.75rem",
                            background: "#ffffff",
                            border: "1px solid #cbd5e1",
                            borderRadius: "0.5rem",
                            color: "#111827",
                            fontFamily: "inherit",
                            resize: "vertical",
                          }}
                          placeholder={`Unesite detaljne informacije o putovanju na ${lang === "hr" ? "hrvatskom" : lang === "en" ? "engleskom" : lang === "de" ? "njemačkom" : "španjolskom"} jeziku...`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  if (editingTravelogue) {
    const t = editingTravelogue;
    const setT = (field, value) => setEditingTravelogue({ ...editingTravelogue, [field]: value });
    const setTitle = (lang, value) => setT("title", { ...(t.title || {}), [lang]: value });
    const setContent = (lang, value) => setT("content", { ...(t.content || {}), [lang]: value });
    return (
      <div className="app admin-app">
        <SEO title="Admin Dashboard - TraveLina" noindex={true} />
        <Navbar />
        <main>
          <section className="section" style={{ paddingTop: "6rem" }}>
            <div className="container">
              <div style={{ marginBottom: "2rem", display: "flex", gap: "1rem" }}>
                <button onClick={handleCancel} style={{ padding: "0.5rem 1rem", background: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: "0.5rem", color: "#334155", cursor: "pointer" }}>← Natrag</button>
                <button onClick={handleSaveTravelogue} style={{ padding: "0.5rem 1rem", background: "#f97316", border: "none", borderRadius: "0.5rem", color: "#111827", cursor: "pointer", fontWeight: "600" }}>Spremi putopis</button>
              </div>
              <div style={{ background: "#ffffff", padding: "2rem", borderRadius: "1rem", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)" }}>
                <h2>Putopis: {t.title?.hr || t.key || "Novi"}</h2>
                <div style={{ display: "grid", gap: "1rem", marginTop: "1rem" }}>
                  <div>
                    <label style={{ display: "block", marginBottom: "0.5rem", color: "#64748b" }}>Key (jedinstveni id)</label>
                    <input value={t.key} onChange={(e) => setT("key", e.target.value)} style={{ width: "100%", padding: "0.75rem", background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "0.5rem", color: "#111827" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", marginBottom: "0.5rem", color: "#64748b" }}>Datum</label>
                    <input type="date" value={t.date} onChange={(e) => setT("date", e.target.value)} style={{ width: "100%", padding: "0.75rem", background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "0.5rem", color: "#111827" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", marginBottom: "0.5rem", color: "#64748b" }}>Slika putopisa</label>
                    {t.image && (
                      <div style={{ marginBottom: "0.75rem" }}>
                        <img src={t.image} alt="Pregled" style={{ maxWidth: "200px", maxHeight: "120px", objectFit: "cover", borderRadius: "0.5rem", border: "1px solid #e2e8f0" }} />
                      </div>
                    )}
                    {hasSupabase && (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          id="travelogue-image-upload"
                          style={{ display: "none" }}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setUploadingTravelogueImage(true);
                            const folder = `travelogues/${(t.key || "new").replace(/\s+/g, "-")}`;
                            const { url, error } = await uploadImage(folder, file);
                            setUploadingTravelogueImage(false);
                            e.target.value = "";
                            if (error) return alert(error.message || "Upload nije uspio.");
                            if (url) setT("image", url);
                          }}
                        />
                        <button
                          type="button"
                          disabled={uploadingTravelogueImage}
                          onClick={() => document.getElementById("travelogue-image-upload").click()}
                          style={{
                            padding: "0.5rem 1rem",
                            marginBottom: "0.5rem",
                            background: uploadingTravelogueImage ? "#6b7280" : "#3b82f6",
                            border: "none",
                            borderRadius: "0.5rem",
                            color: "#fff",
                            cursor: uploadingTravelogueImage ? "not-allowed" : "pointer",
                            fontSize: "0.9rem",
                          }}
                        >
                          {uploadingTravelogueImage ? "Uploadam…" : "Upload sliku s računala"}
                        </button>
                      </>
                    )}
                    <input value={t.image} onChange={(e) => setT("image", e.target.value)} placeholder={hasSupabase ? "Ili zalijepi URL" : "Putanja ili URL slike"} style={{ width: "100%", padding: "0.75rem", background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "0.5rem", color: "#111827" }} />
                  </div>
                  {["hr", "en", "de", "es"].map((lang) => (
                    <div key={lang}>
                      <label style={{ display: "block", marginBottom: "0.5rem", color: "#64748b" }}>Naslov ({lang})</label>
                      <input value={t.title?.[lang] || ""} onChange={(e) => setTitle(lang, e.target.value)} style={{ width: "100%", padding: "0.75rem", background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "0.5rem", color: "#111827" }} />
                    </div>
                  ))}
                  {["hr", "en", "de", "es"].map((lang) => (
                    <div key={lang}>
                      <label style={{ display: "block", marginBottom: "0.5rem", color: "#64748b" }}>Sadržaj ({lang})</label>
                      <textarea value={t.content?.[lang] || ""} onChange={(e) => setContent(lang, e.target.value)} rows={8} style={{ width: "100%", padding: "0.75rem", background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "0.5rem", color: "#111827", resize: "vertical" }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  if (editingDestination) {
    const d = editingDestination;
    const setD = (field, value) => setEditingDestination({ ...editingDestination, [field]: value });
    const setTitle = (lang, value) => setD("title", { ...(d.title || {}), [lang]: value });
    const setText = (lang, value) => setD("text", { ...(d.text || {}), [lang]: value });
    return (
      <div className="app admin-app">
        <SEO title="Admin Dashboard - TraveLina" noindex={true} />
        <Navbar />
        <main>
          <section className="section" style={{ paddingTop: "6rem" }}>
            <div className="container">
              <div style={{ marginBottom: "2rem", display: "flex", gap: "1rem" }}>
                <button onClick={handleCancel} style={{ padding: "0.5rem 1rem", background: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: "0.5rem", color: "#334155", cursor: "pointer" }}>← Natrag</button>
                <button onClick={handleSaveDestination} style={{ padding: "0.5rem 1rem", background: "#f97316", border: "none", borderRadius: "0.5rem", color: "#111827", cursor: "pointer", fontWeight: "600" }}>Spremi destinaciju</button>
              </div>
              <div style={{ background: "#ffffff", padding: "2rem", borderRadius: "1rem", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)" }}>
                <h2>Destinacija: {d.title?.hr || d.title?.en || "Nova"}</h2>
                <div style={{ display: "grid", gap: "1rem", marginTop: "1rem" }}>
                  <div>
                    <label style={{ display: "block", marginBottom: "0.5rem", color: "#64748b" }}>Slika kartice</label>
                    {d.image && (
                      <div style={{ marginBottom: "0.75rem" }}>
                        <img
                          src={d.image}
                          alt="Pregled"
                          style={{ maxWidth: "240px", maxHeight: "140px", objectFit: "cover", borderRadius: "0.5rem", border: "1px solid #e2e8f0" }}
                        />
                      </div>
                    )}
                    {hasSupabase && (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          id="destination-image-upload"
                          style={{ display: "none" }}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setUploadingDestinationImage(true);
                            const folder = `destinations/${String(d.id || "new").replace(/\s+/g, "-")}`;
                            const { url, error } = await uploadImage(folder, file);
                            setUploadingDestinationImage(false);
                            e.target.value = "";
                            if (error) return alert(error.message || "Upload nije uspio.");
                            if (url) setD("image", url);
                          }}
                        />
                        <button
                          type="button"
                          disabled={uploadingDestinationImage}
                          onClick={() => document.getElementById("destination-image-upload").click()}
                          style={{
                            padding: "0.5rem 1rem",
                            marginBottom: "0.5rem",
                            background: uploadingDestinationImage ? "#6b7280" : "#2563eb",
                            border: "none",
                            borderRadius: "0.5rem",
                            color: "#fff",
                            cursor: uploadingDestinationImage ? "not-allowed" : "pointer",
                            fontSize: "0.9rem",
                          }}
                        >
                          {uploadingDestinationImage ? "Uploadam…" : "Upload s računala"}
                        </button>
                      </>
                    )}
                    <input
                      value={d.image || ""}
                      onChange={(e) => setD("image", e.target.value)}
                      placeholder={hasSupabase ? "Ili zalijepi URL slike (npr. iz Supabase Storage)" : "URL slike"}
                      style={{ width: "100%", padding: "0.75rem", background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "0.5rem", color: "#111827" }}
                    />
                    <p style={{ marginTop: "0.35rem", fontSize: "0.8rem", color: "#64748b" }}>
                      Ako je URL postavljen, koristi se umjesto CSS pozadine ispod.
                    </p>
                  </div>
                  <div>
                    <label style={{ display: "block", marginBottom: "0.5rem", color: "#64748b" }}>CSS klasa pozadine (kad nema URL slike; npr. dest-1, dest-2)</label>
                    <input value={d.image_class || "dest-1"} onChange={(e) => setD("image_class", e.target.value)} style={{ width: "100%", padding: "0.75rem", background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "0.5rem", color: "#111827" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", marginBottom: "0.5rem", color: "#64748b" }}>Redoslijed</label>
                    <input type="number" value={d.sort_order ?? 0} onChange={(e) => setD("sort_order", parseInt(e.target.value) || 0)} style={{ width: "100%", padding: "0.75rem", background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "0.5rem", color: "#111827" }} />
                  </div>
                  {["hr", "en", "de", "es"].map((lang) => (
                    <div key={lang}>
                      <label style={{ display: "block", marginBottom: "0.5rem", color: "#64748b" }}>Naslov ({lang})</label>
                      <input value={d.title?.[lang] || ""} onChange={(e) => setTitle(lang, e.target.value)} style={{ width: "100%", padding: "0.75rem", background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "0.5rem", color: "#111827" }} />
                    </div>
                  ))}
                  {["hr", "en", "de", "es"].map((lang) => (
                    <div key={lang}>
                      <label style={{ display: "block", marginBottom: "0.5rem", color: "#64748b" }}>Opis ({lang})</label>
                      <textarea value={d.text?.[lang] || ""} onChange={(e) => setText(lang, e.target.value)} rows={3} style={{ width: "100%", padding: "0.75rem", background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "0.5rem", color: "#111827", resize: "vertical" }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app admin-app">
      <SEO title="Admin Dashboard - TraveLina" noindex={true} />
      <Navbar />
      <main>
        <section className="section" style={{ paddingTop: "6rem" }}>
          <div className="container">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "2rem",
                flexWrap: "wrap",
                gap: "1rem",
              }}
            >
              <div>
                <h1>Admin Dashboard</h1>
                {(currentUser.name || currentUser.email) && (
                  <p style={{ color: "#64748b", fontSize: "0.9rem", marginTop: "0.25rem" }}>
                    Prijavljen kao: {currentUser.name || currentUser.email}
                  </p>
                )}
              </div>
              <button
                onClick={handleLogout}
                style={{
                  padding: "0.5rem 1rem",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "0.5rem",
                  color: "#b91c1c",
                  cursor: "pointer",
                }}
              >
                Odjava
              </button>
            </div>

            {saved && (
              <div
                style={{
                  padding: "1rem",
                  background: "#ecfdf5",
                  border: "1px solid #6ee7b7",
                  borderRadius: "0.5rem",
                  color: "#047857",
                  marginBottom: "1rem",
                }}
              >
                Promjene su spremljene!
              </div>
            )}

            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
              <button
                onClick={() => setActiveTab("tours")}
                style={{
                  padding: "0.5rem 1rem",
                  background: activeTab === "tours" ? "#f97316" : "#f1f5f9",
                  border: "1px solid " + (activeTab === "tours" ? "#f97316" : "#cbd5e1"),
                  borderRadius: "0.5rem",
                  color: activeTab === "tours" ? "#111827" : "#64748b",
                  cursor: "pointer",
                  fontWeight: activeTab === "tours" ? "600" : "400",
                }}
              >
                Putovanja
              </button>
              <button
                onClick={() => setActiveTab("travelogues")}
                style={{
                  padding: "0.5rem 1rem",
                  background: activeTab === "travelogues" ? "#f97316" : "#f1f5f9",
                  border: "1px solid " + (activeTab === "travelogues" ? "#f97316" : "#cbd5e1"),
                  borderRadius: "0.5rem",
                  color: activeTab === "travelogues" ? "#111827" : "#64748b",
                  cursor: "pointer",
                  fontWeight: activeTab === "travelogues" ? "600" : "400",
                }}
              >
                Putopisi
              </button>
              <button
                onClick={() => setActiveTab("destinations")}
                style={{
                  padding: "0.5rem 1rem",
                  background: activeTab === "destinations" ? "#f97316" : "#f1f5f9",
                  border: "1px solid " + (activeTab === "destinations" ? "#f97316" : "#cbd5e1"),
                  borderRadius: "0.5rem",
                  color: activeTab === "destinations" ? "#111827" : "#64748b",
                  cursor: "pointer",
                  fontWeight: activeTab === "destinations" ? "600" : "400",
                }}
              >
                Destinacije
              </button>
            </div>

            {activeTab === "tours" && (
            <div style={{ display: "grid", gap: "1.5rem" }}>
              {hasSupabase && (
                <div style={{ marginBottom: "1rem" }}>
                  <button
                    onClick={() => setEditingTour({
                      key: "",
                      category: "city",
                      days: 1,
                      nights: 0,
                      country: "",
                      date: new Date().toISOString().slice(0, 10),
                      price: 0,
                      tag: "",
                      image: "",
                      blogText: { hr: "", en: "", de: "", es: "" },
                      gallery: [],
                      detailedInfo: { hr: "", en: "", de: "", es: "" },
                    })}
                    style={{ padding: "0.5rem 1rem", background: "#22c55e", border: "none", borderRadius: "0.5rem", color: "#fff", cursor: "pointer", fontWeight: "600" }}
                  >
                    + Dodaj putovanje
                  </button>
                </div>
              )}
              {localTours.map((tour) => (
                <div
                  key={tour.id}
                  style={{
                    background: "#ffffff",
                    padding: "1.5rem",
                    borderRadius: "1.5rem",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <h3>{TOUR_TITLES[tour.key]?.hr || tour.key}</h3>
                    <p style={{ color: "#64748b", fontSize: "0.9rem" }}>
                      {tour.country} · {tour.days} dana / {tour.nights} noćenja · {tour.price}€
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                      onClick={() => handleEdit(tour)}
                      style={{
                        padding: "0.5rem 1rem",
                        background: "#f97316",
                        border: "none",
                        borderRadius: "0.5rem",
                        color: "#111827",
                        cursor: "pointer",
                        fontWeight: "600",
                      }}
                    >
                      Uredi
                    </button>
                    {hasSupabase && (
                      <button
                        onClick={async () => {
                          if (window.confirm("Obrisati ovo putovanje?")) {
                            await deleteTour(tour.id);
                            await refreshTours();
                          }
                        }}
                        style={{ padding: "0.5rem 1rem", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "0.5rem", color: "#b91c1c", cursor: "pointer" }}
                      >
                        Obriši
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            )}

            {activeTab === "travelogues" && (
              <div style={{ display: "grid", gap: "1.5rem" }}>
                {hasSupabase && (
                  <div style={{ marginBottom: "1rem" }}>
                    <button
                      onClick={() => setEditingTravelogue({
                        key: "novi-putopis-" + Date.now(),
                        title: { hr: "", en: "", de: "", es: "" },
                        date: new Date().toISOString().slice(0, 10),
                        image: "",
                        content: { hr: "", en: "", de: "", es: "" },
                      })}
                      style={{ padding: "0.5rem 1rem", background: "#22c55e", border: "none", borderRadius: "0.5rem", color: "#fff", cursor: "pointer", fontWeight: "600" }}
                    >
                      + Dodaj putopis
                    </button>
                  </div>
                )}
                {(travelogues || []).map((t) => (
                  <div
                    key={t.id}
                    style={{
                      background: "#ffffff",
                      padding: "1.5rem",
                      borderRadius: "1rem",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <h3>{t.title?.hr || t.title?.en || t.key}</h3>
                      <p style={{ color: "#64748b", fontSize: "0.9rem" }}>{t.date}</p>
                    </div>
                    {hasSupabase && (
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button onClick={() => setEditingTravelogue({ ...t })} style={{ padding: "0.5rem 1rem", background: "#f97316", border: "none", borderRadius: "0.5rem", color: "#111827", cursor: "pointer", fontWeight: "600" }}>Uredi</button>
                        <button onClick={async () => { if (window.confirm("Obrisati putopis?")) { await deleteTravelogue(t.id); await refreshTravelogues(); } }} style={{ padding: "0.5rem 1rem", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "0.5rem", color: "#b91c1c", cursor: "pointer" }}>Obriši</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeTab === "destinations" && (
              <div style={{ display: "grid", gap: "1.5rem" }}>
                {hasSupabase && (
                  <div style={{ marginBottom: "1rem" }}>
                    <button
                      onClick={() => setEditingDestination({
                        title: { hr: "", en: "", de: "", es: "" },
                        text: { hr: "", en: "", de: "", es: "" },
                        image_class: "dest-1",
                        image: "",
                        sort_order: (destinations || []).length,
                      })}
                      style={{ padding: "0.5rem 1rem", background: "#22c55e", border: "none", borderRadius: "0.5rem", color: "#fff", cursor: "pointer", fontWeight: "600" }}
                    >
                      + Dodaj destinaciju
                    </button>
                  </div>
                )}
                {(destinations || []).map((d) => (
                  <div
                    key={d.id}
                    style={{
                      background: "#ffffff",
                      padding: "1.5rem",
                      borderRadius: "1rem",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <h3>{d.title?.hr || d.title?.en || "Destinacija"}</h3>
                      <p style={{ color: "#64748b", fontSize: "0.9rem" }}>{d.image_class}</p>
                    </div>
                    {hasSupabase && (
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button onClick={() => setEditingDestination({ ...d })} style={{ padding: "0.5rem 1rem", background: "#f97316", border: "none", borderRadius: "0.5rem", color: "#111827", cursor: "pointer", fontWeight: "600" }}>Uredi</button>
                        <button onClick={async () => { if (window.confirm("Obrisati destinaciju?")) { await deleteDestination(d.id); await refreshDestinations(); } }} style={{ padding: "0.5rem 1rem", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "0.5rem", color: "#b91c1c", cursor: "pointer" }}>Obriši</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

